import {Injectable} from '@angular/core'
import {ActivatedRouteSnapshot, RouterStateSnapshot} from '@angular/router'
import {EMPTY, mergeMap, Observable, of} from 'rxjs'
import {ResolverUtilService} from '../../../../core/resolver/resolver-util.service'
import {PUBLIC_ROUTES} from '../../../../shared/routing/public.routes'
import {LinkService} from '../../../../core/i18n/link.service'
import {ArticleContentService} from '../../../../core/content/services/article-content.service'
import {Language} from '../../../../core/i18n/language'
import {TranslocoService} from '@ngneat/transloco'
import {ArticleLanguageAvailability} from '../../../../core/content/types/article.types'

/**
 * The purpose of this resolver is to redirect the user to the article page if he changes languages while being on
 * the article page unavailable page. When the user changes languages, the article page might become available, so we
 * redirect him for a good experience. The user could also use the buttons on a page.
 */
@Injectable()
export class ArticleLanguageUnavailableResolver {
  constructor(
    private readonly contentService: ArticleContentService,
    private readonly linkService: LinkService,
    private readonly util: ResolverUtilService,
    private readonly localeService: TranslocoService,
  ) {}

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<ArticleLanguageAvailability> {
    const slug = route.paramMap.get('pageSlug')
    if (!slug) {
      return this.util.redirectToNotFound(state.url)
    }
    return this.contentService.getArticlePageLanguageAvailability(slug).pipe(
      mergeMap(page => {
        if (page.activeLocales.includes(this.localeService.getActiveLang() as Language)) {
          return this.redirectToArticle(slug)
        } else {
          return of(page)
        }
      }),
    )
  }

  private redirectToArticle(slug: string): Observable<never> {
    this.linkService.navigateTo(PUBLIC_ROUTES.ARTICLE_PAGE, {routeParams: {pageSlug: slug}})
    return EMPTY
  }
}
